import React, { useMemo } from 'react';
import { cn } from '../ui';
import { formatSvaras } from './formatSvaras';

/**
 * Render a single svara line with proper bar separators, matching jnana-native:
 *   - svara tokens are UPPERCASED (TextTransformCase.UPPER)
 *   - if the bhajan's talam beatPattern is supplied, we use formatSvaras() to
 *     align svara groups to the beat marks (`,` slots), so | and ‖ bars land
 *     where they should — same as jnana-native's auto-format logic.
 *   - `|`          → single bar   — gold, prominent
 *   - `‖` or `||`  → double bar   — gold, prominent
 *   - any leftover comma after formatting → faint beat-rest tick
 *
 * Tokens are space-separated. Notes use a monospaced bold font matching
 * native's SVARA_LINE typography style.
 */
export interface SvaraLineProps {
  text: string;
  beatPattern?: string | null;
  fontSize?: number;
  className?: string;
}

type Token = { kind: 'note'; text: string } | { kind: 'beat' } | { kind: 'bar' } | { kind: 'double-bar' };

function tokenize(line: string): Token[] {
  // Match native: uppercase first, then normalize "||" to "‖" for consistent rendering.
  const norm = line.toUpperCase().replace(/\|\|/g, '‖');
  const out: Token[] = [];
  for (const raw of norm.split(/\s+/)) {
    if (!raw) continue;
    if (raw === ',') out.push({ kind: 'beat' });
    else if (raw === '|') out.push({ kind: 'bar' });
    else if (raw === '‖') out.push({ kind: 'double-bar' });
    else out.push({ kind: 'note', text: raw });
  }
  return out;
}

export const SvaraLine: React.FC<SvaraLineProps> = ({ text, beatPattern, fontSize, className }) => {
  const tokens = useMemo(() => {
    // Auto-format svara groups to align with the talam's beat marks if a
    // beatPattern is provided; otherwise render as-is.
    const formatted = beatPattern ? formatSvaras(text, beatPattern) : text;
    return tokenize(formatted);
  }, [text, beatPattern]);
  return (
    <p
      className={cn('font-mono font-semibold tracking-wide', className)}
      style={{ fontSize, lineHeight: 1.65 }}
      aria-label="Svara notation"
    >
      {tokens.map((t, i) => {
        if (t.kind === 'beat') {
          return (
            <span key={i} className="text-text-tertiary opacity-60 mx-1">
              ,
            </span>
          );
        }
        if (t.kind === 'bar') {
          return (
            <span key={i} className="text-accent-gold mx-1.5 font-bold">
              |
            </span>
          );
        }
        if (t.kind === 'double-bar') {
          return (
            <span key={i} className="text-accent-gold mx-1.5 font-bold">
              ‖
            </span>
          );
        }
        return (
          <span key={i} className="text-text-secondary">
            {t.text}
            {i < tokens.length - 1 ? ' ' : ''}
          </span>
        );
      })}
    </p>
  );
};
